import React, { useState, useRef, useEffect } from 'react';
import { Mic, Square, Play, Pause, Trash2, Volume2 } from 'lucide-react';
import { Button } from './ui/button';
import { VoiceRecorder } from '../utils/mediaUtils';

/**
 * VoiceNote Component
 * Allows recording, playback, and management of voice notes
 * 
 * @param {string} value - Base64 encoded audio data
 * @param {function} onChange - Callback when audio data changes
 * @param {string} label - Optional label for the voice note
 * @param {boolean} compact - Compact mode for inline use
 */
export default function VoiceNote({ value, onChange, label = "Voice Note", compact = false }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const recorderRef = useRef(null);
  const audioRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    recorderRef.current = new VoiceRecorder();
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, []);

  // Calculate duration when value changes
  useEffect(() => {
    if (value && audioRef.current) {
      audioRef.current.src = value;
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current.duration);
      };
    }
  }, [value]);

  const startRecording = async () => {
    const success = await recorderRef.current.startRecording();
    if (success) {
      setIsRecording(true);
      setRecordingTime(0);
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }
    const result = await recorderRef.current.stopRecording();
    setIsRecording(false);
    if (result && result.base64) {
      onChange(result.base64);
    }
  };

  const togglePlayback = () => {
    if (!audioRef.current || !value) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const deleteRecording = () => {
    onChange(null);
    setDuration(0);
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (compact) {
    return (
      <div className="flex items-center gap-2">
        <audio ref={audioRef} onEnded={handleAudioEnded} className="hidden" />
        
        {!value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={isRecording ? stopRecording : startRecording}
            className={`${isRecording ? 'bg-red-50 border-red-300 text-red-600' : 'border-purple-300 text-purple-600 hover:bg-purple-50'}`}
          >
            {isRecording ? (
              <>
                <Square className="w-3 h-3 mr-1 fill-current" />
                {formatTime(recordingTime)}
              </>
            ) : (
              <>
                <Mic className="w-3 h-3 mr-1" />
                Record
              </>
            )}
          </Button>
        ) : (
          <div className="flex items-center gap-1 bg-purple-50 rounded-full px-2 py-1">
            <button
              type="button"
              onClick={togglePlayback}
              className="p-1 hover:bg-purple-100 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="w-4 h-4 text-purple-600" />
              ) : (
                <Play className="w-4 h-4 text-purple-600" />
              )}
            </button>
            <span className="text-xs text-purple-600 font-medium">
              {formatTime(duration)}
            </span>
            <button
              type="button"
              onClick={deleteRecording}
              className="p-1 hover:bg-red-100 rounded-full transition-colors"
            >
              <Trash2 className="w-3 h-3 text-red-500" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-slate-700 flex items-center gap-2">
        <Volume2 className="w-4 h-4 text-purple-500" />
        {label}
      </label>
      
      <audio ref={audioRef} onEnded={handleAudioEnded} className="hidden" />
      
      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-sm border border-slate-200">
        {!value ? (
          // Recording UI
          <div className="flex items-center gap-3 w-full">
            <Button
              type="button"
              variant={isRecording ? "destructive" : "outline"}
              size="sm"
              onClick={isRecording ? stopRecording : startRecording}
              className={isRecording ? '' : 'border-purple-400 text-purple-600 hover:bg-purple-50'}
            >
              {isRecording ? (
                <>
                  <Square className="w-4 h-4 mr-2 fill-current" />
                  Stop Recording
                </>
              ) : (
                <>
                  <Mic className="w-4 h-4 mr-2" />
                  Start Recording
                </>
              )}
            </Button>
            
            {isRecording && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
                <span className="text-sm font-mono text-red-600">
                  {formatTime(recordingTime)}
                </span>
              </div>
            )}
            
            {!isRecording && (
              <span className="text-xs text-slate-500">
                Add voice note for reviewer
              </span>
            )}
          </div>
        ) : (
          // Playback UI
          <div className="flex items-center justify-between w-full">
            <div className="flex items-center gap-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={togglePlayback}
                className="border-purple-400 text-purple-600 hover:bg-purple-50"
              >
                {isPlaying ? (
                  <>
                    <Pause className="w-4 h-4 mr-2" />
                    Pause
                  </>
                ) : (
                  <>
                    <Play className="w-4 h-4 mr-2" />
                    Play
                  </>
                )}
              </Button>
              
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-purple-500 rounded-full" />
                <span className="text-sm text-slate-600">
                  Voice note recorded ({formatTime(duration)})
                </span>
              </div>
            </div>
            
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={deleteRecording}
              className="text-red-500 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        )}
      </div>
      
      <p className="text-xs text-slate-500">
        Record a voice note to provide additional details for the review engineer
      </p>
    </div>
  );
}

/**
 * VoiceNotePlayer Component
 * Read-only playback for viewing recorded voice notes
 */
export function VoiceNotePlayer({ audioData, label = "Voice Note" }) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    if (audioData && audioRef.current) {
      audioRef.current.src = audioData;
      audioRef.current.onloadedmetadata = () => {
        setDuration(audioRef.current.duration);
      };
    }
  }, [audioData]);

  const togglePlayback = () => {
    if (!audioRef.current || !audioData) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (!audioData) return null;

  return (
    <div className="inline-flex items-center gap-2 bg-purple-50 border border-purple-200 rounded-full px-3 py-1.5">
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} className="hidden" />
      
      <button
        type="button"
        onClick={togglePlayback}
        className="p-1 hover:bg-purple-100 rounded-full transition-colors"
      >
        {isPlaying ? (
          <Pause className="w-4 h-4 text-purple-600" />
        ) : (
          <Play className="w-4 h-4 text-purple-600" />
        )}
      </button>
      
      <Volume2 className="w-3 h-3 text-purple-500" />
      
      <span className="text-xs text-purple-700 font-medium">
        {label} ({formatTime(duration)})
      </span>
    </div>
  );
}
